import {
    useLayoutEffect,
    useState
} from "react";
import {
    TouchableOpacity,
    View
} from "react-native";
import stylesheet from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme,
    NCoreUIKitToast,
    PageContainer,
    TextAreaInput,
    TextInput,
    Button,
    Text,
    Dialog
} from "ncore-ui-kit";
import {
    useGlobalState
} from "../../../context";
import {
    useDebouncedDimensions
} from "../../../utils";
import {
    useNavigation,
} from "@react-navigation/native";
import {
    useSafeAreaInsets
} from "react-native-safe-area-context";
import type {
    SendContactFormWithAuthRequestType
} from "../../../services/restAPI/actions/sendContactFormWithAuth/types";
import type AppStackParamList from "../../../types/navigation/app";
import type {
    NativeStackNavigationProp
} from "@react-navigation/native-stack";
import {
    ChevronLeft,
    UserIcon,
    Phone,
    Mail,
    Check
} from "lucide-react-native";
import {
    RESTService
} from "../../../services/restAPI";

const ContactFormForMembers = () => {
    const {
        windowWidth
    } = useDebouncedDimensions();

    const navigation = useNavigation<NativeStackNavigationProp<AppStackParamList>>();

    const {
        radiuses,
        spaces,
        colors
    } = NCoreUIKitTheme.useContext();

    const {
        localize
    } = NCoreUIKitLocalize.useContext();

    const [globalState] = useGlobalState();

    const [data,
        setData] = useState<SendContactFormWithAuthRequestType>({
            message: "",
            subject: ""
        });
    const [loading,
        setLoading] = useState(false);
    const [successDialog,
        setSuccessDialog] = useState(false);

    const inset = useSafeAreaInsets();

    useLayoutEffect(() => {
        navigation.setOptions({
            headerShown: true,
            header: () => <View
                style={[
                    stylesheet.headerContainer,
                    {
                        backgroundColor: colors.content.container.default,
                        paddingHorizontal: spaces.spacingMd,
                        paddingTop: inset.top
                    }
                ]}
            >
                <TouchableOpacity
                    style={[
                        stylesheet.backButton,
                        {
                            padding: spaces.spacingSm
                        }
                    ]}
                    onPress={() => {
                        navigation.goBack();
                    }}
                >
                    <ChevronLeft
                        size={30}
                    />
                </TouchableOpacity>
                <View
                    pointerEvents="none"
                    style={[
                        stylesheet.headerTitleContainer,
                        {
                            top: inset.top + spaces.spacingSm
                        }
                    ]}
                >
                    <Text
                        variant="titleLargeSize"
                        color="high"
                        style={stylesheet.headerTitle}
                    >
                        {localize("contact_form")}
                    </Text>
                </View>
            </View>
        });
    }, [navigation]);

    const submitForm = () => {
        setLoading(true);
        const params: SendContactFormWithAuthRequestType = {
            subject: data.subject.trim(),
            message: data.message.trim()
        };
        RESTService.action("SendContactFormWithAuth", params)
            .then((res) => {
                if(res.code === 200 || res.code === 201 || res.code === 202) {
                    setSuccessDialog(true);
                }
            }).catch((err) => {
                console.log(err);
            }).finally(()=>{
                setLoading(false);
            });
    };

    const renderInfo = () => {
        return <View
            style={[
                stylesheet.infoCardContainer,
                {
                    backgroundColor: colors.content.container.mid,
                    width: windowWidth - spaces.spacingLg * 2,
                    marginTop: spaces.spacingLg,
                    borderRadius: radiuses.sm,
                    padding: spaces.spacingLg
                }
            ]}
        >
            <View
                style={[
                    stylesheet.iconContent,
                    {
                        backgroundColor: colors.content.container.success,
                        borderRadius: radiuses.full
                    }
                ]}
            >
                <UserIcon
                    color={colors.content.icon.success}
                    size={30}
                />
            </View>
            <View
                style={[
                    stylesheet.textWrapper,
                    {
                        paddingHorizontal: spaces.spacingLg
                    }
                ]}
            >
                <Text
                    variant="titleLargeSize"
                    color="high"
                >
                    {localize("contact-us")}
                </Text>
                <Text
                    variant="titleSmallSize"
                    color="low"
                >
                    {localize("contact-us-info")}
                </Text>
            </View>
        </View>;
    };

    const isFormValid = Boolean(
        data.subject && data.subject.trim().length > 0 &&
        data.message && data.message.trim().length > 0
    );

    const renderForm = () => {
        return <View
            style={{
                marginTop: spaces.spacingLg
            }}
        >
            <TextInput
                title={localize("name-surname")}
                type="default"
                spreadBehaviour="stretch"
                placeholder={globalState.user?.fullName}
                isDisabled={true}
                icon={() => <UserIcon
                    color={colors.content.icon.success}
                    size={20}
                />}
                style={{
                    marginTop: spaces.spacingMd
                }}
            />
            <TextInput
                title={localize("phone")}
                type="default"
                spreadBehaviour="stretch"
                placeholder={globalState.user?.phoneNumber}
                isDisabled={true}
                icon={() => <Phone
                    color={colors.content.icon.success}
                    size={20}
                />}
                style={{
                    marginTop: spaces.spacingMd
                }}
            />
            <TextInput
                title={localize("e-mail")}
                type="default"
                spreadBehaviour="stretch"
                placeholder={globalState.user?.mail}
                isDisabled={true}
                icon={() => <Mail
                    color={colors.content.icon.success}
                    size={20}
                />}
                style={{
                    marginTop: spaces.spacingMd
                }}
            />
            <TextInput
                title={localize("subject")}
                type={data.subject && data.subject.trim().length === 0 ? "danger" : "default"}
                hintText={data.subject !== undefined && data.subject.trim().length === 0 ? (localize("subjectRequired")) : undefined}
                spreadBehaviour="stretch"
                placeholder={localize("subject")}
                isDisabled={loading}
                onChangeText={(e) => {
                    setData({
                        ...data,
                        subject: e
                    });
                }}
                style={{
                    marginTop: spaces.spacingMd
                }}
            />
            <TextAreaInput
                title={localize("message")}
                type={data.message && data.message.trim().length === 0 ? "danger" : "default"}
                hintText={data.message !== undefined && data.message.trim().length === 0 ? (localize("messageRequired")) : undefined}
                spreadBehaviour="stretch"
                isDisabled={loading}
                placeholder={localize("message")}
                onChangeText={(e) => {
                    setData({
                        ...data,
                        message: e
                    });
                }}
                maxHeight={200}
                style={{
                    marginBottom: spaces.spacingMd
                }}
            />
            <View
                style={stylesheet.contactRow}
            >
                <View
                    style={[
                        stylesheet.infoContent,
                        {
                            backgroundColor: colors.content.container.default,
                            width: windowWidth / 2 - spaces.spacingMd * 2,
                            borderRadius: radiuses.sm,
                            padding: spaces.spacingMd
                        }
                    ]}
                >
                    <Phone
                        color={colors.content.icon.low}
                        size={18}
                    />
                    <View
                        style={{
                            marginLeft: spaces.spacingMd
                        }}
                    >
                        <Text
                            variant="titleSmallSize"
                            color="low"
                        >
                            {localize("phone")}
                        </Text>
                        <Text
                            variant="titleSmallSize"
                            color="high"
                        >
                            {"0332 000 00 00 "}
                        </Text>
                    </View>
                </View>
                <View
                    style={[
                        stylesheet.infoContent,
                        {
                            backgroundColor: colors.content.container.default,
                            width: windowWidth / 2 - spaces.spacingMd * 2,
                            marginLeft: spaces.spacingLg,
                            borderRadius: radiuses.sm,
                            padding: spaces.spacingMd
                        }
                    ]}
                >
                    <Mail
                        color={colors.content.icon.low}
                        size={18}
                    />
                    <View
                        style={{
                            marginLeft: spaces.spacingMd
                        }}
                    >
                        <Text
                            variant="titleSmallSize"
                            color="low"
                        >
                            {localize("e-mail")}
                        </Text>
                        <Text
                            variant="titleSmallSize"
                            color="high"
                        >
                            {"info@musiad.org"}
                        </Text>
                    </View>
                </View>
            </View>
            <Button
                title={localize("submit")}
                type="primary"
                size="large"
                spreadBehaviour="stretch"
                customColor="brandPrimary"
                customBorderColor="brandPrimary"
                style={[
                    stylesheet.submitButton,
                    {
                        marginVertical: spaces.spacingXl
                    }
                ]}
                isLoading={loading}
                isDisabled={!isFormValid}
                onPress={() => {
                    if(!isFormValid) {
                        NCoreUIKitToast.open({
                            title: localize("error"),
                            subTitle: localize("pleaseFillAllFields"),
                            type: "danger"
                        });
                        return;
                    }
                    submitForm();
                }}
            />
        </View>;
    };

    const renderSuccessDialog = () => {
        return <Dialog
            headerComponent={<View
                style={stylesheet.dialogHeaderContainer}
            >
                <View
                    style={[
                        stylesheet.dialogIconWrapper,
                        {
                            backgroundColor: colors.content.container.success,
                            padding: spaces.spacingMd,
                            borderRadius: radiuses.full,
                            marginBottom: spaces.spacingLg
                        }
                    ]}
                >
                    <Check
                        size={30}
                        color={colors.content.icon.success}
                    />
                </View>
                <Text
                    variant="titleLargeSize"
                    color="high"
                >
                    {localize("submittedSuccessfully")}
                </Text>
            </View>}
            isVisible={successDialog}
            onOverlayPress={() => {
                setSuccessDialog(false);
                navigation.goBack();
            }}
            primaryButtonProps={{
                onPress: () => {
                    setSuccessDialog(false);
                    navigation.goBack();
                },
                title: localize("backToHome"),
                type: "success"
            }}
            variant="ok"
        />;
    };

    return <PageContainer
        backgroundColor="subtle"
        isScrollable={true}
        scrollViewProps={{
            showsVerticalScrollIndicator: false
        }}
    >
        {renderInfo()}
        {renderForm()}
        {renderSuccessDialog()}
    </PageContainer>;
};
export default ContactFormForMembers;
